"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { generateJSON } from "@tiptap/core";
import type { JSONContent } from "@tiptap/react";
import { useState } from "react";
import { EmailPreviewIframe } from "@/components/email-preview-iframe";
import { useInViewport } from "@/hooks/use-in-viewport";
import { cx } from "@/lib/tailwind-utils";
import type { EmailTemplate } from "@/types/email-template";
import { prepareEmailHtml } from "./blocks/html-fallback/prepare-html";
import { getHtmlParseExtensions } from "./blocks/registry";
import { normalizeEmailContent } from "./blocks/row/normalize-content";
import { DEFAULT_STYLE_SETTINGS } from "./context";
import { renderEmailHtml } from "./converter/render-to-html";
import type { TiptapEmailJSON } from "./types/tiptap-json";
import { emailTemplateQueryKeys } from "@/query/email-template";

/** Parse a stored template body (serialized Tiptap JSON) into a document. */
export function parseTemplateBody(body: string): JSONContent | undefined {
  try {
    return JSON.parse(body) as JSONContent;
  } catch {
    return undefined;
  }
}

/**
 * Renders a template's saved body to preview HTML.
 *
 * `renderEmailHtml` is a network round-trip and a grid needs one per template,
 * so this caches indefinitely rather than re-rendering on every mount. Saving an
 * edit still refreshes it: the key sits under `emailTemplateQueryKeys.all`,
 * which the create/update/archive mutations already invalidate wholesale.
 *
 * Pass `enabled: false` to hold the round-trip back — {@link TemplatePreview}
 * uses it to keep off-screen cards off the network entirely.
 */
export function useTemplatePreviewHtml(template, options) {
  const $ = _c(9);
  let t0;
  if ($[0] !== template.id) {
    t0 = [...emailTemplateQueryKeys.detail(template.id), "preview"];
    $[0] = template.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== template.body || $[3] !== template.bodyFormat) {
    t1 = async () => {
      if (template.bodyFormat === "HTML") {
        const parsed = generateJSON(prepareEmailHtml(template.body), getHtmlParseExtensions());
        const normalized = normalizeEmailContent(parsed as JSONContent);
        return renderEmailHtml(normalized as TiptapEmailJSON, DEFAULT_STYLE_SETTINGS);
      }
      const doc = parseTemplateBody(template.body);
      if (!doc) {
        return "";
      }
      return renderEmailHtml(doc as TiptapEmailJSON, DEFAULT_STYLE_SETTINGS);
    };
    $[2] = template.body;
    $[3] = template.bodyFormat;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = options?.enabled ?? true;
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: Infinity,
      enabled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}

/**
 * A scaled-down render of the real email, shared by the templates settings grid
 * and the composer's template picker so both show the same thing. Inert, so the
 * surrounding card takes the click rather than the iframe.
 *
 * Each preview costs a POST to /api/email/render plus an iframe, so the render
 * is deferred until the card is near the viewport — a library's worth of cards
 * mounting at once would otherwise fire a round-trip apiece. `staleTime:
 * Infinity` means a card that scrolls away and back doesn't pay twice.
 */
export function TemplatePreview(t0) {
  const $ = _c(11);
  const {
    template,
    className
  } = t0;
  const [isInView, setIsInView] = useState(false);
  const viewportRef = useInViewport(setIsInView);
  let t1;
  if ($[0] !== isInView) {
    t1 = {
      enabled: isInView
    };
    $[0] = isInView;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: html
  } = useTemplatePreviewHtml(template, t1);
  let t2;
  if ($[2] !== className) {
    t2 = cx("pointer-events-none overflow-hidden bg-background-secondary", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = html ?? "";
  const t4 = `${template.name} preview`;
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <div className="h-[400px] w-[250%] origin-top-left scale-[0.4]"><EmailPreviewIframe html={t3} className="size-full border-0" title={t4} /></div>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t5 || $[9] !== viewportRef) {
    t6 = <div ref={viewportRef} className={t2}>{t5}</div>;
    $[7] = t2;
    $[8] = t5;
    $[9] = viewportRef;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}